import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {faGraduationCap} from "@fortawesome/free-solid-svg-icons";
import {useUserCatalog, useUserLearning} from "../store/user";
import LearnerShell from "../components/learner/LearnerShell";
import ResourceCard from "../components/learner/ResourceCard";
import EmptyState from "../components/learner/EmptyState";
import TabStrip from "../components/learner/TabStrip";
import {imageUrl, shortDate} from "../lib/format";

const TABS = [
  {id: "all", label: "All Courses"},
  {id: "in-progress", label: "In Progress"},
  {id: "completed", label: "Completed"},
  {id: "not-started", label: "Not Started"},
];

const progressOf = (course) => Math.round(Number(course?.progress) || 0);
const isCompleted = (course) => Boolean(course?.completed) || progressOf(course) >= 100;

/**
 * /my-courses — every course the learner is enrolled in.
 *
 *   GET /api/learners/courses/enrolled?limit
 *   GET /api/learners/categories
 *
 * The enrolled endpoint's strict schema rejects unknown query keys (422), so
 * the search box and topic dropdown are applied client-side below.
 */
const MyCourses = () => {
  const navigate = useNavigate();
  const {
    enrolledCourses,
    enrolledCoursesLoading,
    enrolledCoursesError,
    fetchEnrolledCourses,
  } = useUserLearning();
  const {userCategories, userCategoriesLoading, fetchUserCategories} = useUserCatalog();
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");

  useEffect(() => {
    fetchUserCategories();
  }, [fetchUserCategories]);

  // Debounced so typing does not spam the endpoint. Only `limit` is sent —
  // `categoryId`/`search` are not part of the endpoint's contract.
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEnrolledCourses({limit: 60});
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchEnrolledCourses]);

  // Search + topic filtering, applied locally over the enrolled payload.
  const visibleCourses = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return enrolledCourses.filter((course) => {
      if (categoryId) {
        const courseCategoryId = course?.categoryId ?? course?.category?.id;
        if (courseCategoryId !== categoryId) return false;
      }
      if (!needle) return true;
      return [course?.title, course?.tutor?.name, course?.tutor?.fullName].some(
        (value) => typeof value === "string" && value.toLowerCase().includes(needle)
      );
    });
  }, [enrolledCourses, search, categoryId]);

  const filtered = useMemo(() => {
    if (tab === "in-progress") return visibleCourses.filter((c) => !isCompleted(c) && progressOf(c) > 0);
    if (tab === "completed") return visibleCourses.filter(isCompleted);
    if (tab === "not-started") return visibleCourses.filter((c) => !isCompleted(c) && progressOf(c) === 0);
    return visibleCourses;
  }, [tab, visibleCourses]);

  return (
    <LearnerShell
      title="My Courses"
      subtitle="Everything you are enrolled in — continue where you left off."
      icon={faGraduationCap}
      onSearchChange={setSearch}
      actions={
        <>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search my courses…"
            className="w-52 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-300 sm:w-64"
          />
          <select
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            className="rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-blue-300"
          >
            <option value="">{userCategoriesLoading ? "Loading topics…" : "All topics"}</option>
            {userCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </>
      }
    >
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <TabStrip
          tabs={TABS.map((entry) => ({
            ...entry,
            count: visibleCourses.filter((course) => {
              if (entry.id === "completed") return isCompleted(course);
              if (entry.id === "in-progress") return !isCompleted(course) && progressOf(course) > 0;
              if (entry.id === "not-started") return !isCompleted(course) && progressOf(course) === 0;
              return true;
            }).length,
          }))}
          active={tab}
          onChange={setTab}
        />

        <div className="p-4 sm:p-6">
          {enrolledCoursesError ? (
            <p className="m-0 mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
              {enrolledCoursesError}
            </p>
          ) : null}

          {enrolledCoursesLoading && enrolledCourses.length === 0 ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[0, 1, 2, 3].map((index) => (
                <div key={index} className="h-80 animate-pulse rounded-2xl border border-slate-200 bg-white" />
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((course) => {
                const id = course?.courseId ?? course?.id;
                const progress = progressOf(course);
                return (
                  <ResourceCard
                    key={id}
                    image={imageUrl(course?.coverImage)}
                    title={course?.title || "Untitled course"}
                    subtitle={course?.tutor?.name || course?.tutor?.fullName || "Fillop"}
                    badge={isCompleted(course) ? "Completed" : `${progress}%`}
                    meta={[
                      `Progress ${progress}%`,
                      course?.lastAccessedAt
                        ? `Last opened ${shortDate(course.lastAccessedAt)}`
                        : course?.enrolledAt
                          ? `Enrolled ${shortDate(course.enrolledAt)}`
                          : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    owned
                    primaryLabel={progress > 0 && !isCompleted(course) ? "Continue" : "Open course"}
                    onPrimary={() => navigate(`/content-library/course/${id}/read`)}
                    secondaryLabel="Details"
                    onSecondary={() => navigate(`/content-library/course/${id}`)}
                    onOpen={() => navigate(`/content-library/course/${id}`)}
                  />
                );
              })}
            </div>
          ) : (
            <EmptyState
              icon={faGraduationCap}
              title={search || categoryId ? "No enrolled courses match your filters" : "You are not enrolled yet"}
              hint={
                search || categoryId
                  ? "Clear the search or pick another topic."
                  : "Courses you buy or are invited to will appear here."
              }
              action={
                <button
                  type="button"
                  onClick={() => navigate("/courses")}
                  className="rounded-md bg-blue-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
                >
                  Browse courses
                </button>
              }
            />
          )}
        </div>
      </section>
    </LearnerShell>
  );
};

export default MyCourses;
